import React, { useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Layout from "./components/Layout";
import ScrollToTop from "./components/ScrollToTop";
import ProtectedRoute from "./components/ProtectedRoute";
import RequireRole from "./components/RequireRole";
import SplashScreen from "./components/SplashScreen";
import Dashboard from "./pages/Dashboard";
import SubcontractorDashboard from "./pages/SubcontractorDashboard";
import Timesheets from "./pages/Timesheets";
import Settings from "./pages/Settings";
import MyDocuments from "./pages/MyDocuments";
import Login from "./pages/Login";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import CRM from "./pages/CRM";
import CompanyDetail from "./pages/CompanyDetail";
import ContactDetail from "./pages/ContactDetail";
import QuoteDetail from "./pages/QuoteDetail";
import SubcontractorQuotes from "./pages/SubcontractorQuotes";
import SubcontractorQuoteDetail from "./pages/SubcontractorQuoteDetail";
import Field from "./pages/Field";
import FieldSchedule from "./pages/FieldSchedule";
import FieldVehicles from "./pages/FieldVehicles";
import Tasks from "./pages/Tasks";
import Announcements from "./pages/Announcements";
import Holidays from "./pages/Holidays";
import Accountabilities from "./pages/Accountabilities";
import Videos from "./pages/Videos";
import Handbook from "./pages/Handbook";
import Shop from "./pages/Shop";
import TravelerScan from "./pages/TravelerScan";
import LogTime from "./pages/LogTime";
import ScanQR from "./pages/ScanQR";
import MaterialLookup from "./pages/MaterialLookup";
import MaterialSearch from "./pages/MaterialSearch";
import ShopWorkOrders from "./components/Shop/WorkOrders";
import ShopEquipment from "./components/Shop/Equipment";
import ShopBillOfMaterials from "./components/Shop/BillOfMaterials";
import Reports from "./pages/Reports";
import Engineering from "./pages/Engineering";
import ECNs from "./components/Engineering/ECNs";
import EngineeringSchedule from "./components/Engineering/EngineeringSchedule";
import Expenses from "./components/Expenses";
import CompanyCard from "./components/Expenses/CompanyCard";
import Reimbursable from "./components/Expenses/Reimbursable";
import Miles from "./components/Expenses/Miles";
import PerDiem from "./components/Expenses/PerDiem";
import IncidentReport from "./components/Reports/IncidentReport";
import ProjectReport from "./components/Reports/ProjectReport";
import DoorServiceReport from "./components/Reports/DoorServiceReport";
import RFTestReport from "./components/Reports/RFTestReport";

function App() {
    const [showSplash, setShowSplash] = useState(true);

    if (showSplash) {
        return <SplashScreen onFinished={() => setShowSplash(false)} />;
    }

    return (
        <Router>
            <ScrollToTop />
            <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                    path="/*"
                    element={
                        <ProtectedRoute>
                            <Layout>
                                <Routes>
                                    <Route path="/" element={<Dashboard />} />
                                    <Route path="/subcontractor" element={<SubcontractorDashboard />} />
                                    <Route path="/projects" element={<Projects />} />
                                    <Route path="/projects/:id" element={<ProjectDetail />} />
                                    <Route
                                        path="/crm"
                                        element={
                                            <RequireRole allow={["isAdmin", "isManagement"]}>
                                                <CRM />
                                            </RequireRole>
                                        }
                                    />
                                    <Route
                                        path="/crm/companies/:id"
                                        element={
                                            <RequireRole allow={["isAdmin", "isManagement"]}>
                                                <CompanyDetail />
                                            </RequireRole>
                                        }
                                    />
                                    <Route
                                        path="/crm/contacts/:id"
                                        element={
                                            <RequireRole allow={["isAdmin", "isManagement"]}>
                                                <ContactDetail />
                                            </RequireRole>
                                        }
                                    />
                                    <Route
                                        path="/crm/quotes/:id"
                                        element={
                                            <RequireRole allow={["isAdmin", "isManagement"]}>
                                                <QuoteDetail />
                                            </RequireRole>
                                        }
                                    />
                                    <Route
                                        path="/subcontractor/quotes"
                                        element={
                                            <RequireRole allow={["isSubcontractor"]}>
                                                <SubcontractorQuotes />
                                            </RequireRole>
                                        }
                                    />
                                    <Route
                                        path="/subcontractor/quotes/:id"
                                        element={
                                            <RequireRole allow={["isSubcontractor"]}>
                                                <SubcontractorQuoteDetail />
                                            </RequireRole>
                                        }
                                    />
                                    <Route path="/timesheets" element={<Timesheets />} />
                                    <Route path="/settings" element={<Settings />} />
                                    <Route path="/my-documents" element={<MyDocuments />} />
                                    <Route path="/field" element={<Field />} />
                                    <Route path="/field/schedule" element={<FieldSchedule />} />
                                    <Route path="/field/vehicles" element={<FieldVehicles />} />
                                    <Route path="/tasks" element={<Tasks />} />
                                    <Route path="/announcements" element={<Announcements />} />
                                    <Route path="/holidays" element={<Holidays />} />
                                    <Route path="/accountabilities" element={<Accountabilities />} />
                                    <Route path="/videos" element={<Videos />} />
                                    <Route path="/handbook" element={<Handbook />} />
                                    <Route path="/shop" element={<Shop />} />
                                    <Route path="/shop/work-orders" element={<ShopWorkOrders />} />
                                    <Route path="/shop/equipment" element={<ShopEquipment />} />
                                    <Route path="/shop/bom" element={<ShopBillOfMaterials />} />
                                    <Route path="/shop/material" element={<MaterialLookup />} />
                                    <Route path="/shop/material/search" element={<MaterialSearch />} />
                                    <Route path="/scan" element={<ScanQR />} />
                                    <Route path="/traveler/:token" element={<TravelerScan />} />
                                    <Route path="/log-time/:token" element={<LogTime />} />
                                    <Route path="/expenses" element={<Expenses />} />
                                    <Route path="/expenses/company-card" element={<CompanyCard />} />
                                    <Route path="/expenses/reimbursable" element={<Reimbursable />} />
                                    <Route path="/expenses/miles" element={<Miles />} />
                                    <Route path="/expenses/per-diem" element={<PerDiem />} />
                                    <Route path="/reports" element={<Reports />} />
                                    <Route path="/reports/incident" element={<IncidentReport />} />
                                    <Route path="/reports/project" element={<ProjectReport />} />
                                    <Route path="/reports/door-service" element={<DoorServiceReport />} />
                                    <Route path="/reports/rf-test" element={<RFTestReport />} />
                                    <Route path="/engineering" element={<Engineering />} />
                                    <Route path="/engineering/ecns" element={<ECNs />} />
                                    <Route path="/engineering/schedule" element={<EngineeringSchedule />} />

                                    {/* Add your routes here */}
                                </Routes>
                            </Layout>
                        </ProtectedRoute>
                    }
                />
            </Routes>
        </Router>
    );
}

export default App;
